import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, Plus, Download, Search, FileWarning, Mail, MessageCircle, Trash2, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../hooks/use-toast';
import WarningsDialog from '../components/employees/WarningsDialog';
import { openWhatsApp } from '../utils/whatsapp';

const API = process.env.REACT_APP_BACKEND_URL;

const WT_LABEL = { verbal: 'Verbal Warning', written: 'Written Warning', written2: 'Written Warning #2', final: 'Final Warning' };
const WT_COLOR = {
  verbal: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  written: 'bg-orange-100 text-orange-700 border-orange-300',
  written2: 'bg-orange-100 text-orange-800 border-orange-400',
  final: 'bg-red-100 text-red-700 border-red-300',
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const WarningLetters = () => {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Issue flow
  const [pickerOpen, setPickerOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/warning-letters`);
      setLetters(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load warning letters', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [contacts, setContacts] = useState({});
  useEffect(() => {
    axios.get(`${API}/api/employees/contacts`)
      .then(r => setContacts(Object.fromEntries((r.data || []).map(c => [c.id, c]))))
      .catch(() => {});
  }, []);
  const waNumber = (empId) => {
    const c = contacts[empId] || {};
    return (c.whatsapp_no || c.phone || '').trim();
  };

  const [emailingId, setEmailingId] = useState(null);
  const emailLetter = async (l) => {
    setEmailingId(l.warning_id);
    try {
      const res = await axios.post(`${API}/api/warning-letters/${l.employee_id}/${l.warning_id}/email`, {});
      toast({ title: 'Emailed', description: `Sent to ${res.data?.emailed_to || 'employee'}` });
      load();
    } catch (e) {
      toast({ title: 'Email failed', description: e?.response?.data?.detail || 'Could not send email', variant: 'destructive' });
    } finally {
      setEmailingId(null);
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const doDelete = async () => {
    const l = confirmDelete;
    if (!l) return;
    setDeletingId(l.warning_id);
    try {
      await axios.delete(`${API}/api/warning-letters/${l.employee_id}/${l.warning_id}`);
      toast({ title: 'Deleted', description: 'Removed from employee & manager portals. Struck through here.' });
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const restoreLetter = async (l) => {
    setDeletingId(l.warning_id);
    try {
      await axios.post(`${API}/api/warning-letters/${l.employee_id}/${l.warning_id}/restore`, {});
      toast({ title: 'Restored', description: 'Letter is live again on all portals.' });
      load();
    } catch (e) {
      toast({ title: 'Restore failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    if (employees.length === 0) {
      try {
        const res = await axios.get(`${API}/api/employees`);
        const list = (res.data || []).filter(e => e.is_active !== false);
        setEmployees(list);
      } catch {
        toast({ title: 'Error', description: 'Failed to load employees', variant: 'destructive' });
      }
    }
  };

  const pickEmployee = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (emp) {
      setSelectedEmp({ id: emp.id, name: emp.name });
      setPickerOpen(false);
      setDialogOpen(true);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return letters.filter(l => {
      if (typeFilter !== 'all' && l.warning_type !== typeFilter) return false;
      if (!s) return true;
      return (l.employee_name || '').toLowerCase().includes(s)
        || (l.employee_code || '').toLowerCase().includes(s)
        || (l.description || '').toLowerCase().includes(s);
    });
  }, [letters, search, typeFilter]);

  const counts = useMemo(() => {
    const c = { all: 0, verbal: 0, written: 0, written2: 0, final: 0 };
    letters.forEach(l => {
      if (l.deleted) return;
      c.all += 1;
      if (c[l.warning_type] != null) c[l.warning_type] += 1;
    });
    return c;
  }, [letters]);

  return (
    <div className="space-y-6" data-testid="warning-letters-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-yellow-600" /> Warning Letters
          </h1>
          <p className="text-sm text-gray-500">All issued employee discipline forms across staff. Each letter is linked to its employee and the dashboard.</p>
        </div>
        <Button onClick={openPicker} className="bg-yellow-600 hover:bg-yellow-700 gap-1.5" data-testid="issue-warning-letter-btn">
          <Plus className="h-4 w-4" /> Issue Warning Letter
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search by employee, code or description…" value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="warning-search" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px]" data-testid="warning-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types ({counts.all})</SelectItem>
                <SelectItem value="verbal">Verbal ({counts.verbal})</SelectItem>
                <SelectItem value="written">Written ({counts.written})</SelectItem>
                <SelectItem value="written2">Written #2 ({counts.written2})</SelectItem>
                <SelectItem value="final">Final ({counts.final})</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500 flex flex-col items-center gap-2">
              <FileWarning className="h-10 w-10 text-gray-300" />
              No warning letters found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 border-b">
                    <th className="py-2 pr-3">Employee</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Offenses</th>
                    <th className="py-2 pr-3">Description</th>
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Issued by</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.warning_id} className={`border-b last:border-0 hover:bg-gray-50 ${l.deleted ? 'opacity-60 bg-rose-50/40' : ''}`} data-testid={`warning-row-${l.warning_id}`}>
                      <td className={`py-2.5 pr-3 ${l.deleted ? 'line-through' : ''}`}>
                        <div className="font-medium">{l.employee_name}</div>
                        <div className="text-xs text-gray-400">{l.employee_code} · {l.department}</div>
                      </td>
                      <td className="py-2.5 pr-3">
                        <Badge variant="outline" className={`text-[11px] ${WT_COLOR[l.warning_type] || ''} ${l.deleted ? 'line-through' : ''}`}>
                          {WT_LABEL[l.warning_type] || l.warning_type}
                        </Badge>
                        {l.deleted && (
                          <Badge className="bg-rose-600 text-[10px] ml-1" data-testid={`row-deleted-${l.warning_id}`}>Deleted</Badge>
                        )}
                        {l.suspension_enabled && l.suspension_days && (
                          <Badge className="bg-red-600 text-[10px] ml-1" data-testid={`row-suspension-${l.warning_id}`}>
                            Susp {l.suspension_days}d
                          </Badge>
                        )}
                      </td>
                      <td className={`py-2.5 pr-3 text-xs text-gray-600 max-w-[180px] ${l.deleted ? 'line-through' : ''}`}>
                        {[...(l.offense_types || []), ...(l.custom_offenses || [])].length
                          ? [...(l.offense_types || []).map(o => o.replace(/_/g, ' ')), ...(l.custom_offenses || [])].join(', ')
                          : '—'}
                      </td>
                      <td className={`py-2.5 pr-3 text-gray-700 max-w-[260px] truncate ${l.deleted ? 'line-through' : ''}`} title={l.description}>{l.description || '—'}</td>
                      <td className={`py-2.5 pr-3 whitespace-nowrap ${l.deleted ? 'line-through' : ''}`}>{fmtDate(l.issue_date)}</td>
                      <td className={`py-2.5 pr-3 text-gray-600 ${l.deleted ? 'line-through' : ''}`}>{l.issued_by || '—'}</td>
                      <td className="py-2.5 pr-3">
                        {l.deleted ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : l.acknowledged ? (
                          <Badge className="bg-emerald-600 text-[10px]">Acknowledged</Badge>
                        ) : l.emailed_at ? (
                          <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Awaiting ack</Badge>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        {l.deleted ? (
                          <Button variant="ghost" size="sm" className="text-emerald-700"
                            disabled={deletingId === l.warning_id}
                            onClick={() => restoreLetter(l)} title="Restore letter"
                            data-testid={`warning-restore-${l.warning_id}`}>
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="sm" className="text-indigo-600"
                              disabled={emailingId === l.warning_id}
                              onClick={() => emailLetter(l)} title="Email PDF to employee"
                              data-testid={`warning-email-${l.warning_id}`}>
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-green-700"
                              onClick={() => openWhatsApp(waNumber(l.employee_id), `Hi ${l.employee_name || ''}, sharing your Employee Discipline / Warning Letter. Please find it attached.`)}
                              title="Send via WhatsApp"
                              data-testid={`warning-whatsapp-${l.warning_id}`}>
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-rose-600"
                              onClick={() => window.open(`${API}/api/warning-letters/${l.employee_id}/${l.warning_id}/pdf`, '_blank')}
                              title="Download PDF"
                              data-testid={`warning-pdf-${l.warning_id}`}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-rose-700"
                              disabled={deletingId === l.warning_id}
                              onClick={() => setConfirmDelete(l)} title="Delete letter"
                              data-testid={`warning-delete-${l.warning_id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee picker for issuing */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md" data-testid="employee-picker-dialog">
          <DialogHeader><DialogTitle>Select Employee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select onValueChange={pickEmployee}>
              <SelectTrigger data-testid="employee-picker-select"><SelectValue placeholder="Choose an employee…" /></SelectTrigger>
              <SelectContent className="max-h-[320px]">
                {employees.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.name} {e.employee_code ? `· ${e.employee_code}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>

      {selectedEmp && (
        <WarningsDialog
          open={dialogOpen}
          onOpenChange={(v) => { setDialogOpen(v); if (!v) { setSelectedEmp(null); load(); } }}
          employee={selectedEmp}
          onSuccess={load}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="warning-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this warning letter?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete ? (
                <>This will remove <b>{confirmDelete.employee_name}</b>'s {WT_LABEL[confirmDelete.warning_type] || 'warning'} from the employee and manager portals and the Discipline tracker. It stays struck-through here and can be restored anytime.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="warning-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="warning-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WarningLetters;
